'use client';

import { diffLines, diffWordsWithSpace } from 'diff';

export type CsvParseResult = {
  delimiter: ',' | ';' | '\t' | '|';
  headers: string[];
  rows: string[][];
};

export type DataDiffChunk = {
  type: 'added' | 'removed' | 'unchanged';
  text: string;
};

export type DataDiffLine = {
  type: 'added' | 'removed' | 'unchanged';
  chunks: DataDiffChunk[];
};

export type DataRegexMatch = {
  match: string;
  index: number;
  groups: string[];
};

function normalizeLineEndings(value: string) {
  return value.replace(/\r\n?/g, '\n');
}

function escapeHtml(value: string) {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function isFlatValue(value: unknown) {
  return (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
}

function parseJson(value: string) {
  try {
    return { parsed: JSON.parse(value), error: null as string | null };
  } catch (error) {
    return {
      parsed: null,
      error: error instanceof Error ? error.message : 'The JSON could not be parsed.',
    };
  }
}

export function validateJsonText(value: string) {
  const { parsed, error } = parseJson(value);
  if (error) {
    throw new Error(error);
  }

  return JSON.stringify(parsed, null, 2);
}

export function formatJsonText(value: string) {
  return validateJsonText(value);
}

export function minifyJsonText(value: string) {
  const { parsed, error } = parseJson(value);
  if (error) {
    throw new Error(error);
  }

  return JSON.stringify(parsed);
}

function normalizeFlatJsonRows(value: unknown) {
  const rows = Array.isArray(value) ? value : [value];
  if (!rows.length) {
    throw new Error('Add at least one JSON row first.');
  }

  const normalized = rows.map((row) => {
    if (!row || Array.isArray(row) || typeof row !== 'object') {
      throw new Error('Use one flat object or an array of flat objects in v1.');
    }

    const record = row as Record<string, unknown>;
    for (const [key, entry] of Object.entries(record)) {
      if (!isFlatValue(entry)) {
        throw new Error(`Nested JSON is not supported in v1. Problem field: "${key}".`);
      }
    }

    return record;
  });

  return normalized;
}

function detectDelimiter(lines: string[]) {
  const delimiters: Array<',' | ';' | '\t' | '|'> = [',', ';', '\t', '|'];
  let best: ',' | ';' | '\t' | '|' = ',';
  let bestScore = -1;

  for (const delimiter of delimiters) {
    const score = lines
      .slice(0, 5)
      .map((line) => parseCsvLine(line, delimiter).length)
      .reduce((sum, count) => sum + count, 0);

    if (score > bestScore) {
      best = delimiter;
      bestScore = score;
    }
  }

  return best;
}

function parseCsvLine(line: string, delimiter: ',' | ';' | '\t' | '|') {
  const cells: string[] = [];
  let current = '';
  let insideQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const next = line[index + 1];

    if (char === '"') {
      if (insideQuotes && next === '"') {
        current += '"';
        index += 1;
        continue;
      }

      insideQuotes = !insideQuotes;
      continue;
    }

    if (char === delimiter && !insideQuotes) {
      cells.push(current);
      current = '';
      continue;
    }

    current += char;
  }

  cells.push(current);
  return cells.map((cell) => cell.trim());
}

export function parseCsvText(value: string): CsvParseResult {
  const lines = normalizeLineEndings(value)
    .split('\n')
    .filter((line) => line.trim().length > 0);

  if (!lines.length) {
    throw new Error('Paste some CSV first.');
  }

  const delimiter = detectDelimiter(lines);
  const parsedLines = lines.map((line) => parseCsvLine(line, delimiter));
  const headers = parsedLines[0].map((header, index) => header || `column_${index + 1}`);
  const rows = parsedLines.slice(1).map((row) => {
    const next = [...row];
    while (next.length < headers.length) {
      next.push('');
    }
    return next.slice(0, headers.length);
  });

  return {
    delimiter,
    headers,
    rows,
  };
}

function escapeCsvCell(value: unknown) {
  const text = value == null ? '' : String(value);
  if (/[",\n;\t|]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

export function jsonToCsvText(value: string) {
  const { parsed, error } = parseJson(value);
  if (error) {
    throw new Error(error);
  }

  const rows = normalizeFlatJsonRows(parsed);
  const headers = Array.from(
    rows.reduce((set, row) => {
      Object.keys(row).forEach((key) => set.add(key));
      return set;
    }, new Set<string>()),
  );

  const csvLines = [
    headers.map((header) => escapeCsvCell(header)).join(','),
    ...rows.map((row) => headers.map((header) => escapeCsvCell(row[header] ?? '')).join(',')),
  ];

  return csvLines.join('\n');
}

export function csvToJsonText(value: string) {
  const parsed = parseCsvText(value);
  const rows = parsed.rows.map((row) =>
    Object.fromEntries(parsed.headers.map((header, index) => [header, row[index] ?? ''])),
  );

  return JSON.stringify(rows, null, 2);
}

export function extractCsvColumns(value: string, selectedHeaders: string[]) {
  const parsed = parseCsvText(value);
  const headers = parsed.headers.filter((header) => selectedHeaders.includes(header));

  if (!headers.length) {
    throw new Error('Choose at least one CSV column first.');
  }

  const columnIndexes = headers.map((header) => parsed.headers.indexOf(header));
  const rows = parsed.rows.map((row) => columnIndexes.map((index) => row[index] ?? ''));
  const csv = [
    headers.map((header) => escapeCsvCell(header)).join(parsed.delimiter),
    ...rows.map((row) => row.map((cell) => escapeCsvCell(cell)).join(parsed.delimiter)),
  ].join('\n');
  const json = JSON.stringify(
    rows.map((row) =>
      Object.fromEntries(headers.map((header, index) => [header, row[index] ?? ''])),
    ),
    null,
    2,
  );

  return {
    csv,
    json,
    headers,
    rows,
    delimiter: parsed.delimiter,
  };
}

export function dedupeLines(value: string) {
  const lines = normalizeLineEndings(value).split('\n');
  const seen = new Set<string>();
  const next: string[] = [];
  let removedCount = 0;

  for (const line of lines) {
    if (seen.has(line)) {
      removedCount += 1;
      continue;
    }
    seen.add(line);
    next.push(line);
  }

  return {
    text: next.join('\n'),
    removedCount,
  };
}

export function sortLines(value: string, options: { descending: boolean; caseSensitive: boolean }) {
  const lines = normalizeLineEndings(value).split('\n');
  const collator = new Intl.Collator(undefined, {
    sensitivity: options.caseSensitive ? 'variant' : 'base',
    numeric: true,
  });
  const sorted = [...lines].sort((left, right) => collator.compare(left, right));

  if (options.descending) {
    sorted.reverse();
  }

  return sorted.join('\n');
}

export function removeEmptyLines(value: string) {
  return normalizeLineEndings(value)
    .split('\n')
    .filter((line) => line.trim().length > 0)
    .join('\n');
}

export function createTextDiff(left: string, right: string) {
  const changes = diffLines(normalizeLineEndings(left), normalizeLineEndings(right));
  const lines: DataDiffLine[] = [];
  const summary = { addedLines: 0, removedLines: 0, changedBlocks: 0 };

  for (let index = 0; index < changes.length; index += 1) {
    const part = changes[index];

    if (part.added) {
      summary.addedLines += part.count ?? part.value.split('\n').filter(Boolean).length;
      summary.changedBlocks += 1;
      lines.push({ type: 'added', chunks: [{ type: 'added', text: part.value }] });
      continue;
    }

    if (part.removed) {
      const next = changes[index + 1];
      summary.removedLines += part.count ?? part.value.split('\n').filter(Boolean).length;
      summary.changedBlocks += 1;

      if (next?.added) {
        const wordChanges = diffWordsWithSpace(part.value, next.value);
        const removedChunks: DataDiffChunk[] = [];
        const addedChunks: DataDiffChunk[] = [];

        for (const chunk of wordChanges) {
          if (chunk.added) {
            addedChunks.push({ type: 'added', text: chunk.value });
          } else if (chunk.removed) {
            removedChunks.push({ type: 'removed', text: chunk.value });
          } else {
            removedChunks.push({ type: 'unchanged', text: chunk.value });
            addedChunks.push({ type: 'unchanged', text: chunk.value });
          }
        }

        lines.push({ type: 'removed', chunks: removedChunks });
        lines.push({ type: 'added', chunks: addedChunks });
        summary.addedLines += next.count ?? next.value.split('\n').filter(Boolean).length;
        index += 1;
      } else {
        lines.push({ type: 'removed', chunks: [{ type: 'removed', text: part.value }] });
      }
      continue;
    }

    lines.push({ type: 'unchanged', chunks: [{ type: 'unchanged', text: part.value }] });
  }

  return {
    lines,
    summary,
    summaryText: [
      `Added lines: ${summary.addedLines}`,
      `Removed lines: ${summary.removedLines}`,
      `Changed blocks: ${summary.changedBlocks}`,
    ].join('\n'),
  };
}

export function renderTextDiffHtml(diff: ReturnType<typeof createTextDiff>) {
  return diff.lines
    .map((line) => {
      const className =
        line.type === 'added'
          ? 'tool-markdown-diff-line tool-markdown-diff-line-added'
          : line.type === 'removed'
            ? 'tool-markdown-diff-line tool-markdown-diff-line-removed'
            : 'tool-markdown-diff-line';
      const content = line.chunks
        .map((chunk) => {
          const chunkClassName =
            chunk.type === 'added'
              ? 'tool-markdown-diff-chunk tool-markdown-diff-chunk-added'
              : chunk.type === 'removed'
                ? 'tool-markdown-diff-chunk tool-markdown-diff-chunk-removed'
                : 'tool-markdown-diff-chunk';
          return `<span class="${chunkClassName}">${escapeHtml(chunk.text).replace(/\n/g, '<br />')}</span>`;
        })
        .join('');
      return `<div class="${className}">${content || '&nbsp;'}</div>`;
    })
    .join('');
}

export function testRegexPattern(pattern: string, flags: string, text: string) {
  let regex: RegExp;

  try {
    regex = new RegExp(pattern, flags);
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : 'The regular expression could not be compiled.',
    );
  }

  const iterableRegex = regex.global ? regex : new RegExp(pattern, `${flags}g`);
  const matches: DataRegexMatch[] = [];
  let match: RegExpExecArray | null = iterableRegex.exec(text);

  while (match) {
    matches.push({
      match: match[0],
      index: match.index,
      groups: match.slice(1),
    });

    if (match[0] === '') {
      iterableRegex.lastIndex += 1;
    }

    match = iterableRegex.exec(text);
  }

  return {
    matches,
    source: regex.source,
    flags: regex.flags,
  };
}

export function createDataToolJson(value: unknown) {
  return JSON.stringify(value, null, 2);
}
